import React, { useState } from "react";
import { useDispatch } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import {
  appApi,
  useGetNcsUploadsQuery,
  useGetNcsPartnersQuery,
  useGetNcsDiagQuery,
  useGetAllProjectsForMatrixQuery,
} from "../../../../store/appApi";
import {
  usePartnerCode,
  PARTNER_CODES,
  panelClass,
  fieldClass,
  labelClass,
  btnPrimary,
  btnGhost,
  Badge,
  fmtDate,
  fmtDateInput,
} from "./config";
import {
  uploadCustomerStatusSheet,
  rerunDiffApi,
  createPartnerApi,
  bulkUploadSheetsApi,
  getSnapshotStorageApi,
  copySnapshotsToFilesApi,
  dropSnapshotCollectionApi,
} from "../../../../services/netplusCustomerStatus";
import { uiConfirm, uiAlert, uiToast } from "./ui";
import DuplicateCustomersModal from "./DuplicateCustomersModal";

const PartnerSetup = ({ partnerCode, onDone }) => {
  const { data: projData } = useGetAllProjectsForMatrixQuery();
  const projects = projData?.data ?? projData?.projects ?? [];
  const [projectId, setProjectId] = useState("");
  const [saving, setSaving] = useState(false);

  const register = async () => {
    if (!projectId) {
      uiToast({ type: "warning", title: "Pick the AdminProject to map to." });
      return;
    }
    try {
      setSaving(true);
      await createPartnerApi({ partnerCode, projectId });
      uiToast({ type: "success", title: `Partner ${partnerCode} registered` });
      onDone();
    } catch (e) {
      uiToast({
        type: "error",
        title: "Could not register partner",
        message:
          e?.response?.data?.message ||
          "Super-admin only. Check you are signed in as super-admin.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5">
      <div className="text-sm font-semibold text-amber-900">
        One-time setup — partner “{partnerCode}” is not registered yet
      </div>
      <p className="mt-1 text-xs text-amber-800">
        Map it to the existing AdminProject under the Netplus hub (super-admin only).
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <span className={labelClass}>AdminProject</span>
          <select
            className={`${fieldClass} w-72`}
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">Select project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName} {p.city ? `· ${p.city}` : ""}
              </option>
            ))}
          </select>
        </div>
        <button className={btnPrimary} disabled={saving} onClick={register}>
          {saving ? "Registering…" : `Register ${partnerCode}`}
        </button>
      </div>
    </div>
  );
};

const NcsUpload = () => {
  const dispatch = useDispatch();
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [file, setFile] = useState(null);
  const [snapshotDate, setSnapshotDate] = useState(fmtDateInput());
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  // Customer ids in the sheet that already exist under another partner — the
  // upload waits here until the user says which partner is the right one.
  const [conflicts, setConflicts] = useState(null);

  const { data, isLoading } = useGetNcsUploadsQuery({ partnerCode });
  const uploads = data?.uploads || [];

  const { data: partnersData, refetch: refetchPartners } =
    useGetNcsPartnersQuery();
  const partnerRegistered = (partnersData?.partners || []).some(
    (p) => p.partnerCode === partnerCode,
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!partnerRegistered) {
      uiToast({
        type: "warning",
        title: `Register partner "${partnerCode}" first`,
        message: "Use the one-time setup box above — pick its AdminProject and click Register.",
      });
      return;
    }
    if (!file) {
      uiToast({ type: "warning", title: "Pick a sheet file first (.xlsx)." });
      return;
    }
    await runUpload();
  };

  const runUpload = async (resolutions) => {
    try {
      setSaving(true);
      setResult(null);
      const res = await uploadCustomerStatusSheet({ file, snapshotDate, partnerCode, resolutions });
      setResult(res.data);
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      uiToast({ type: "success", title: res.data?.message || "Sheet accepted." });
      const cleanup = res.data?.duplicateCleanup;
      if (cleanup?.removed?.length || cleanup?.kept?.length) {
        uiToast({
          type: "info",
          title: `Duplicates: ${cleanup.removed.length} removed from the other partner, ${cleanup.kept.length} kept`,
        });
      }
      setFile(null);
    } catch (err) {
      const payload = err?.response?.data;
      if (err?.response?.status === 409 && payload?.requiresResolution) {
        setConflicts(payload.conflicts || []);
        return;
      }
      setResult(payload || null);
      uiAlert({
        type: "error",
        title: "Upload rejected",
        message:
          payload?.upload?.validation?.errors?.length
            ? payload.upload.validation.errors
            : payload?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  const rerun = async (id) => {
    const ok = await uiConfirm({
      title: "Re-run the diff for this sheet?",
      message: "Best-effort — telecaller-worked follow-ups are preserved.",
      confirmText: "Re-run",
    });
    if (!ok) return;
    try {
      const res = await rerunDiffApi(id, partnerCode);
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      const diff = res.data?.diff || {};
      uiToast({
        type: "success",
        title: "Diff re-run",
        message: `+${diff.followupsOpened ?? 0} opened · −${
          diff.followupsClosed ?? 0
        } closed · ${diff.transitionsCreated ?? 0} transitions`,
      });
    } catch (err) {
      uiToast({
        type: "error",
        title: "Could not re-run",
        message: err?.response?.data?.message || "Something went wrong.",
      });
    }
  };

  return (
    <>
      <PageTitle activeMenu="Upload Sheet" motherMenu="Netplus" />

      {partnersData && !partnerRegistered && (
        <PartnerSetup
          partnerCode={partnerCode}
          onDone={() => {
            refetchPartners();
            dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
          }}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <section className={`${panelClass} p-6`}>
          <h2 className="text-lg font-semibold text-gray-900">Upload daily sheet</h2>
          <p className="mt-1 text-xs text-gray-500">
            The sheet is parsed, validated and diffed against the previous day
            automatically.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-4">
            <div>
              <span className={labelClass}>Partner code</span>
              <select
                className={fieldClass}
                value={partnerCode}
                onChange={(e) => setPartnerCode(e.target.value)}
              >
                {(partnersData?.partners?.length
                  ? partnersData.partners.map((p) => p.partnerCode)
                  : PARTNER_CODES
                ).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span className={labelClass}>Sheet date (as-of)</span>
              <input
                type="date"
                className={fieldClass}
                value={snapshotDate}
                max={fmtDateInput()}
                onChange={(e) => setSnapshotDate(e.target.value)}
              />
            </div>
            <div>
              <span className={labelClass}>File (.xlsx)</span>
              <input
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className={`${fieldClass} file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-50 file:px-3 file:py-1.5 file:text-cyan-700`}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
            <button
              type="submit"
              className={btnPrimary}
              disabled={saving || (partnersData && !partnerRegistered)}
              title={
                partnersData && !partnerRegistered
                  ? `Register partner "${partnerCode}" above first`
                  : undefined
              }
            >
              {saving ? "Uploading…" : "Upload & diff"}
            </button>
            {partnersData && !partnerRegistered && (
              <p className="text-xs text-amber-700">
                Register “{partnerCode}” above before uploading its sheet.
              </p>
            )}
          </form>

          {result && (
            <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs">
              <div className="font-semibold text-gray-700">
                {result.status ? "Accepted" : "Rejected"}
              </div>
              {result.summary && (
                <ul className="mt-2 space-y-1 text-gray-600">
                  <li>Rows parsed: {result.summary.rowsParsed}</li>
                  <li>Row delta: {result.summary.rowCountDelta}</li>
                  {result.summary.diff && (
                    <li>
                      Diff — +{result.summary.diff.followupsOpened} opened, −
                      {result.summary.diff.followupsClosed} closed,{" "}
                      {result.summary.diff.transitionsCreated} transitions
                    </li>
                  )}
                  {(result.summary.warnings || []).map((w, i) => (
                    <li key={i} className="text-amber-700">
                      ⚠ {w}
                    </li>
                  ))}
                </ul>
              )}
              {result.upload?.validation?.errors?.length > 0 && (
                <ul className="mt-2 space-y-1 text-rose-700">
                  {result.upload.validation.errors.map((x, i) => (
                    <li key={i}>✕ {x}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>

        <section className={panelClass}>
          <div className="border-b border-gray-100 px-5 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
              Recent uploads
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3 text-left">Sheet date</th>
                  <th className="px-4 py-3 text-center">State</th>
                  <th className="px-4 py-3 text-center">Rows</th>
                  <th className="px-4 py-3 text-center">Δ</th>
                  <th className="px-4 py-3 text-center">Parse</th>
                  <th className="px-4 py-3 text-center">Transitions</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                      Loading…
                    </td>
                  </tr>
                ) : uploads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                      No uploads yet.
                    </td>
                  </tr>
                ) : (
                  uploads.map((u) => (
                    <tr key={u._id} className="hover:bg-cyan-50/30">
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {fmtDate(u.snapshotDate)}
                        {!u.isCurrent && (
                          <span className="ml-2 text-[10px] uppercase text-gray-400">
                            superseded
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge
                          tone={
                            u.state === "diffed"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : u.state === "failed"
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-gray-100 text-gray-600 border-gray-200"
                          }
                        >
                          {u.state}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums">{u.rowsParsed}</td>
                      <td className="px-4 py-3 text-center tabular-nums">
                        {u.rowCountDelta > 0 ? `+${u.rowCountDelta}` : u.rowCountDelta}
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums">
                        {((u.parseRate || 0) * 100).toFixed(0)}%
                      </td>
                      <td className="px-4 py-3 text-center tabular-nums">
                        {u.transitionsCreated ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {u.isCurrent && u.state !== "failed" && (
                          <button className={btnGhost} onClick={() => rerun(u._id)}>
                            Re-run diff
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <DuplicateCustomersModal
        conflicts={conflicts}
        onCancel={() => setConflicts(null)}
        onConfirm={(resolutions) => {
          setConflicts(null);
          runUpload(resolutions);
        }}
      />

      <BatchUploadPanel dispatch={dispatch} knownCodes={(partnersData?.partners || []).map((p) => p.partnerCode)} />

      <DiagPanel partnerCode={partnerCode} />

      <SnapshotStoragePanel />
    </>
  );
};

const BatchUploadPanel = ({ dispatch, knownCodes }) => {
  const [files, setFiles] = useState([]);
  const [snapshotDate, setSnapshotDate] = useState(fmtDateInput());
  const [saving, setSaving] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [conflicts, setConflicts] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) {
      uiToast({ type: "warning", title: "Pick one or more sheet files (.xlsx)." });
      return;
    }
    await runBatch();
  };

  const runBatch = async (resolutions) => {
    try {
      setSaving(true);
      setBatchResult(null);
      const res = await bulkUploadSheetsApi({ files, snapshotDate, resolutions });
      setBatchResult(res.data);
      dispatch(appApi.util.invalidateTags(["NetplusCustomerStatus"]));
      uiToast({
        type: "success",
        title: `Batch done — ${res.data.accepted} accepted, ${res.data.skipped} skipped, ${res.data.rejected} rejected.`,
      });
      setFiles([]);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.requiresResolution) {
        setConflicts(err.response.data.conflicts || []);
        return;
      }
      uiAlert({
        type: "error",
        title: "Batch upload failed",
        message: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`${panelClass} mt-6 p-6`}>
      <DuplicateCustomersModal
        conflicts={conflicts}
        onCancel={() => setConflicts(null)}
        onConfirm={(resolutions) => {
          setConflicts(null);
          runBatch(resolutions);
        }}
      />
      <h2 className="text-lg font-semibold text-gray-900">Batch upload (multiple partners)</h2>
      <p className="mt-1 text-xs text-gray-500">
        Upload several files at once, or a single workbook with several tabs
        (Sheet1, Sheet2…). Each file/tab is matched to its partner
        automatically from its own banner text — no partner picker needed.
        A tab whose banner doesn't match a registered partner is skipped.
      </p>
      {knownCodes.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-400">
          Registered partner codes: {knownCodes.join(", ")}
        </p>
      )}

      <form onSubmit={submit} className="mt-4 flex flex-wrap items-end gap-3">
        <div>
          <span className={labelClass}>Sheet date (as-of)</span>
          <input
            type="date"
            className={fieldClass}
            value={snapshotDate}
            max={fmtDateInput()}
            onChange={(e) => setSnapshotDate(e.target.value)}
          />
        </div>
        <div>
          <span className={labelClass}>Files (.xlsx) — select multiple</span>
          <input
            type="file"
            multiple
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className={`${fieldClass} file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-50 file:px-3 file:py-1.5 file:text-cyan-700`}
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
        </div>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? "Uploading…" : `Upload batch${files.length ? ` (${files.length})` : ""}`}
        </button>
      </form>

      {batchResult && (
        <div className="mt-5 overflow-x-auto rounded-xl border border-gray-200">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2 text-left">File</th>
                <th className="px-3 py-2 text-left">Tab</th>
                <th className="px-3 py-2 text-left">Partner</th>
                <th className="px-3 py-2 text-left">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {batchResult.results.map((r, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">{r.fileName}</td>
                  <td className="px-3 py-2">{r.worksheetName || "—"}</td>
                  <td className="px-3 py-2">{r.partnerCode || "—"}</td>
                  <td className="px-3 py-2">
                    {r.skipped ? (
                      <span className="text-amber-700">⚠ {r.reason}</span>
                    ) : r.ok ? (
                      <span className="text-emerald-700">
                        ✓ {r.message} ({r.summary?.rowsParsed ?? 0} rows)
                      </span>
                    ) : (
                      <span className="text-rose-700">
                        ✕ {r.message}
                        {r.validation?.errors?.length
                          ? ` — ${r.validation.errors.join("; ")}`
                          : ""}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const DiagPanel = ({ partnerCode }) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching, refetch } = useGetNcsDiagQuery(
    { partnerCode },
    { skip: !open },
  );
  const cmp = data?.comparison;

  return (
    <section className={`${panelClass} mt-6`}>
      <button
        className="flex w-full items-center justify-between px-5 py-3 text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Diagnostics {open ? "▾" : "▸"}
        </span>
        <span className="text-xs text-gray-400">
          why is the report empty?
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100 px-5 py-4 text-xs">
          <button className={btnGhost} onClick={refetch}>
            {isFetching ? "Loading…" : "Refresh"}
          </button>
          {data && (
            <div className="mt-3 space-y-2">
              <div>
                Customers <b>{data.counts?.customerCount}</b> · Transitions{" "}
                <b>{data.counts?.transitionCount}</b> · Follow-ups{" "}
                <b>{data.counts?.followupCount}</b>
              </div>
              <div>Snapshot dates: {(data.snapshotDates || []).join(", ") || "none"}</div>
              <div>
                Transitions by type:{" "}
                {(data.transitionsByType || [])
                  .map((t) => `${t._id}:${t.n}`)
                  .join(" · ") || "none"}
              </div>
              <table className="mt-2 min-w-full">
                <tbody>
                  {(data.uploads || []).map((u) => (
                    <tr key={u._id} className="border-b border-gray-50">
                      <td className="py-1 pr-3">{fmtDate(u.snapshotDate)}</td>
                      <td className="py-1 pr-3">
                        state=<b>{u.state}</b>
                      </td>
                      <td className="py-1 pr-3">rows={u.rowsParsed}</td>
                      <td className="py-1 pr-3">tr={u.transitionsCreated ?? "—"}</td>
                      <td className="py-1 pr-3">
                        diffRunAt={u.diffRunAt ? "yes" : "NO"}
                      </td>
                      <td className="py-1 pr-3 font-medium text-red-700">
                        {(u.validation?.errors || []).join("; ")}
                      </td>
                      <td className="py-1 text-amber-700">
                        {(u.validation?.warnings || []).join("; ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cmp && (
                <div className="mt-3 rounded-lg bg-gray-50 p-3">
                  <div className="font-semibold text-gray-700">
                    {cmp.olderDate} → {cmp.newerDate}
                  </div>
                  <div>
                    matched {cmp.matchedSubscribers} · status changed{" "}
                    {cmp.statusChanged} · activation advanced{" "}
                    {cmp.activationAdvanced} · expiry advanced{" "}
                    {cmp.expiryAdvanced} · identical {cmp.identical}
                  </div>
                  <div className="mt-1 font-medium text-cyan-700">{cmp.verdict}</div>
                  {(cmp.samples || []).length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-gray-500">
                      {cmp.samples.map((s, i) => (
                        <li key={i}>
                          {s.customerKey}: {s.status} | act {s.activation} | exp{" "}
                          {s.expiry}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

// One-time move of the per-day snapshot rows from the database to files on
// the server (super-admin). Covers every partner, not just the selected one.
const SnapshotStoragePanel = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const fail = (title, err) =>
    uiAlert({
      type: "error",
      title,
      message: err?.response?.data?.message || "Something went wrong.",
    });

  const load = async () => {
    setBusy("Loading…");
    try {
      setData((await getSnapshotStorageApi()).data);
    } catch (err) {
      fail("Could not load snapshot storage", err);
    }
    setBusy("");
  };

  const copy = async () => {
    try {
      let st = data;
      do {
        setBusy(`Copying… ${st.copied} of ${st.sheetsInDb} sheets done`);
        st = (await copySnapshotsToFilesApi()).data;
        setData(st);
      } while (st.pending > 0 && !(st.problems || []).length);
      if ((st.problems || []).length) {
        uiAlert({ type: "error", title: "Copy stopped", message: st.problems.join("\n") });
      } else {
        uiToast({
          type: "success",
          title: "Snapshots copied",
          message: `${st.copied} sheets are on the server and match the database.`,
        });
      }
    } catch (err) {
      fail("Copy failed", err);
    }
    setBusy("");
  };

  const drop = async () => {
    const ok = await uiConfirm({
      title: "Remove the snapshot rows from the database?",
      message: `All ${data.copied} sheets are stored as server files and match. This frees about ${mb(
        data.dbSizeBytes,
      )} and cannot be undone.`,
      confirmText: "Remove from database",
      tone: "danger",
    });
    if (!ok) return;
    setBusy("Removing…");
    try {
      const res = (await dropSnapshotCollectionApi()).data;
      setData(res);
      uiToast({
        type: "success",
        title: "Database space freed",
        message: `About ${mb(res.freedBytes)} released.`,
      });
    } catch (err) {
      fail("Could not remove", err);
    }
    setBusy("");
  };

  const ready =
    data && data.collectionExists && data.persistent && !data.pending && !data.mismatches.length;

  return (
    <section className={`${panelClass} mt-6`}>
      <button
        className="flex w-full items-center justify-between px-5 py-3 text-left"
        onClick={() => {
          if (!open && !data) load();
          setOpen((o) => !o);
        }}
      >
        <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Snapshot storage {open ? "▾" : "▸"}
        </span>
        <span className="text-xs text-gray-400">
          move daily snapshots out of the database
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-gray-100 px-5 py-4 text-xs">
          {busy && <div className="text-gray-500">{busy}</div>}
          {data && (
            <>
              <div>
                Server folder: <b>{data.folder}</b>
              </div>
              {!data.persistent && (
                <div className="font-medium text-red-700">
                  PERSIST_DIR is not set on the server — snapshot files cannot be saved
                  safely and uploads will fail until it is.
                </div>
              )}
              {data.collectionExists ? (
                <div>
                  Still in the database: <b>{data.dbRows}</b> rows in{" "}
                  <b>{data.sheetsInDb}</b> sheets ({mb(data.dbSizeBytes)}) · copied to
                  server files: <b>{data.copied}</b> · left to copy: <b>{data.pending}</b>
                </div>
              ) : (
                <div className="font-medium text-cyan-700">
                  Nothing left in the database — snapshots are stored as server files.
                </div>
              )}
              {data.mismatches.map((m) => (
                <div key={m} className="font-medium text-red-700">
                  Does not match its file — {m}
                </div>
              ))}
              {data.missingEverywhere.length > 0 && (
                <div className="text-amber-700">
                  Stored nowhere (re-upload needed): {data.missingEverywhere.join(", ")}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <button className={btnGhost} onClick={load} disabled={Boolean(busy)}>
                  Refresh
                </button>
                {data.collectionExists && data.pending > 0 && (
                  <button
                    className={btnPrimary}
                    onClick={copy}
                    disabled={Boolean(busy) || !data.persistent}
                  >
                    Copy to server files
                  </button>
                )}
                {ready && (
                  <button className={btnPrimary} onClick={drop} disabled={Boolean(busy)}>
                    Remove from database
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

const mb = (n) => `${((n || 0) / 1048576).toFixed(0)} MB`;

export default NcsUpload;
